import { Check, ChevronDown, Building2, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTenantSelection } from '@/contexts/TenantSelectionContext';
import { cn } from '@/lib/utils';

export function TenantSelector() {
  const { t, i18n } = useTranslation('platformAdmin');
  // `error` was not read here before: a rejected fetch left `availableTenants` at its previous
  // (often empty) value, and the dropdown rendered `CommandEmpty` — the same "no results" copy a
  // search with zero matches gets. An admin whose tenant list failed to load saw an empty picker
  // with no sign that anything was wrong, same class of defect as the flows screen fixed in PR 430.
  const { selectedTenant, availableTenants, loading, error, refreshTenants, selectTenant } = useTenantSelection();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  if (loading) {
    return (
      <div className="w-[280px] h-10 bg-muted animate-pulse rounded-md" />
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-[280px] max-w-full justify-between"
        >
          <div className="flex items-center gap-2 min-w-0">
            <Building2 className="h-4 w-4 flex-shrink-0" />
            {selectedTenant ? (
              <div className="flex items-center gap-2 min-w-0">
                <span className="truncate">{selectedTenant.business_name}</span>
                <Badge variant="secondary" className="text-xs">
                  {t(`tenantStatus.${selectedTenant.status}`, selectedTenant.status)}
                </Badge>
              </div>
            ) : (
              <span className="text-muted-foreground">{t('tenantSelector.placeholder')}</span>
            )}
          </div>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
        <PopoverContent className="w-[280px] p-0">
          {error ? (
            <div className="p-3 space-y-2">
              <div className="flex items-start gap-2 text-sm text-destructive">
                <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>{t('tenantSelector.loadFailed')}</span>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => refreshTenants()}
              >
                {t('tenantSelector.retry')}
              </Button>
            </div>
          ) : (
          <Command>
            <CommandInput placeholder={t('tenantSelector.search')} />
            <CommandEmpty>{t('tenantSelector.empty')}</CommandEmpty>
            <CommandGroup>
            {availableTenants.map((tenant) => (
              <CommandItem
                key={tenant.id}
                value={tenant.business_name}
                onSelect={() => {
                  selectTenant(tenant);
                  // Inside a tenant area, switching tenant moves the whole area. Elsewhere
                  // (e.g. the interceptor, which redirects on its own) selection stays local.
                  if (location.pathname.startsWith('/platform-admin/tenant/')) {
                    navigate(`/platform-admin/tenant/${tenant.id}/ai-agents`);
                  }
                  setOpen(false);
                }}
              >
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <Building2 className="h-4 w-4 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="truncate">{tenant.business_name}</div>
                      {tenant.id !== 'platform' && (
                        <div className="text-xs text-muted-foreground">
                          {t('tenantSelector.created', { date: new Date(tenant.created_at).toLocaleDateString(i18n.language) })}
                        </div>
                      )}
                      {tenant.id === 'platform' && (
                        <div className="text-xs text-muted-foreground">
                          {t('tenantSelector.platformLevel')}
                        </div>
                      )}
                    </div>
                    <Badge variant="outline" className="text-xs">
                      {t(`tenantStatus.${tenant.status}`, tenant.status)}
                    </Badge>
                  </div>
                <Check
                  className={cn(
                    "ml-2 h-4 w-4",
                    selectedTenant?.id === tenant.id ? "opacity-100" : "opacity-0"
                  )}
                />
              </CommandItem>
            ))}
          </CommandGroup>
        </Command>
          )}
      </PopoverContent>
    </Popover>
  );
}